class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  // The overlay is re-created every time the drawer contents are re-rendered, so binding is
  // idempotent: without the flag a stack of listeners builds up on repeated cart updates.
  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay') || this.querySelector('.cart-drawer__overlay');
    if (!overlay || overlay.dataset.closeBound === 'true') return;

    overlay.dataset.closeBound = 'true';
    overlay.addEventListener('click', this.close.bind(this));
  }

  connectedCallback() {
    document.addEventListener('cart:rerender', this.handleCartRerender.bind(this));
  }

  handleCartRerender(event) {
    if (event.detail && event.detail.sections) this.renderContents(event.detail);
  }

  setHeaderCartIconAccessibility() {
    // Prefer the cart icon of the header this drawer belongs to, so each instance drives its own
    // trigger instead of every instance piling listeners onto the first icon in the document.
    const scope = this.closest('header') || this.closest('.header-wrapper');
    const cartLink = (scope && scope.querySelector('#cart-icon-bubble')) || document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    if (cartLink.dataset.cartDrawerBound === 'true') return;
    cartLink.dataset.cartDrawerBound = 'true';

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        // The drawer may have been closed again before the transition ended; trapping focus then
        // would leave a keyboard trap on a hidden drawer.
        if (!this.classList.contains('active')) return;

        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : this.querySelector('#CartDrawer') || document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (!containerToTrapFocusOn || !focusElement) return;

        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    // Clear every instance, not just this one: the close button and the overlay each only reach
    // the drawer they live in, so a second instance left with `active` would keep its
    // full-viewport overlay painted above the page.
    document.querySelectorAll('cart-drawer').forEach((drawer) => {
      drawer.classList.remove('active', 'animate');
    });

    removeTrapFocus(this.activeElement);
    this.activeElement = null;
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.contains('is-empty') && this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      // Look inside this instance first so a drawer never re-renders another one's contents.
      const sectionElement = section.selector
        ? this.querySelector(section.selector) || document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.bindOverlay();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class AtcButton extends HTMLElement {
  constructor() {
    super();

    this.handleSubmit = this.handleSubmit.bind(this);
    this.submitForm = this.querySelector('form[action="/cart/add"]');
  }

  connectedCallback() {
    this.submitForm.addEventListener('submit', this.handleSubmit);
  }

  handleSubmit(event) {
    event.preventDefault();

    const variantId = this.submitForm.querySelector('input[name="id"]').value;
    const quantityInput = this.submitForm.querySelector('select[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

    const formData = {
      items: [
        {
          id: variantId,
          quantity: quantity
        }
      ],
      sections: ['cart-drawer', 'cart-icon-bubble'],
    };

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })
      .then(response => response.json())
      .then(data => {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:rerender', {
            detail: data,
            bubbles: true,
          })
        );
      });
  }
}

customElements.define('atc-button', AtcButton);